import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { WmPart } from "../lib/wmParts";
import api from "../lib/api";
import { fmtCur, fmtRate, fmtDate, fmtDateTime } from "../lib/format";
import { ArrowLeft, Package } from "@phosphor-icons/react";

const norm = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

export default function MsDispatchDetail() {
  const { saleNo } = useParams();
  const [inv, setInv] = useState(null);
  const [tab, setTab] = useState("header");
  const [tp, setTp] = useState(null);
  const [containers, setContainers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api.get(`/ms/invoices/${saleNo}`).then((r) => setInv(r.data)).catch(() => setNotFound(true));
  }, [saleNo]);

  useEffect(() => {
    if (!inv?.ms_inv_no) { setContainers([]); return; }
    api.get(`/shipping/by-invoice/${encodeURIComponent(inv.ms_inv_no)}`)
      .then((r) => setContainers(r.data || []))
      .catch(() => setContainers([]));
  }, [inv?.ms_inv_no]);

  useEffect(() => {
    if (!inv?.ms_pcode) { setTp(null); return; }
    api.get("/dispatch/transfer-parties").then((r) => {
      const pk = norm(inv.ms_pcode), nk = norm(inv.customer_name);
      setTp((r.data || []).find((t) => [t.canon_pcode, t.pcode, t.party].map(norm).some((k) => k && (k === pk || k === nk))) || null);
    }).catch(() => setTp(null));
  }, [inv?.ms_pcode]);

  if (notFound) return <Layout breadcrumb={["Home", "Dispatch", saleNo]}><div className="jde-page"><Link to="/dispatch" className="jde-btn jde-btn-sm"><ArrowLeft size={12} weight="bold" /> Back</Link><div className="muted" style={{ marginTop: 20 }}>Invoice #{saleNo} not found.</div></div></Layout>;
  if (!inv) return <Layout breadcrumb={["Home", "Dispatch", saleNo]}>Loading…</Layout>;

  const d = inv.dispatch || {};
  const c = inv.charges || {};
  const t = inv.taxes || {};
  const gst = (t.tcgst_amt || 0) + (t.tsgst_amt || 0) + (t.tigst_amt || 0);
  const disp = inv.display || {};
  const isExp = disp.is_export;
  const chargesInr = (c.freight_amt || 0) + (c.packing_amt || 0) + (c.ins_amt || 0) + (c.other_amt || 0);

  return (
    <Layout breadcrumb={["Home", "Dispatch", `#${saleNo}`]}>
      <div className="jde-page">
        <div className="jde-toolbar">
          <Link to="/dispatch" className="jde-btn jde-btn-sm" data-testid="back-to-dispatch">
            <ArrowLeft size={12} weight="bold" /> Back
          </Link>
          <div style={{ marginLeft: 12, fontFamily: "var(--jde-font-mono)", fontSize: 13 }}>
            <strong>Sale #{inv.ms_sale_no}</strong>
            {inv.ms_inv_no ? <span className="muted" style={{ marginLeft: 12 }}>Invoice #{inv.ms_inv_no}</span> : null}
            <span className="muted" style={{ marginLeft: 12 }}>Customer: <strong>{inv.customer_name}</strong></span>
            <span className="jde-badge confirmed" style={{ marginLeft: 12 }}>Plant {inv.comp_code}</span>
            {tp && <span className="jde-badge" data-testid="disp-detail-tp" style={{ marginLeft: 12, background: "#0F766E", color: "white", fontWeight: 700 }} title={`Ordered by ${tp.party || inv.customer_name}`}>Transfer Party ↳ {tp.tp_name || tp.tp_code}</span>}
            {inv.ms_pono && (
              <Link to={`/pos/${inv.ms_pono}`} style={{ marginLeft: 12, color: "var(--omh-primary)", fontWeight: 600 }}>
                ↑ open PO #{inv.ms_pono}
              </Link>
            )}
            {containers.map((ct) => (
              <Link key={ct.container_no} to={`/shipping/${encodeURIComponent(ct.container_no)}`}
                data-testid={`disp-detail-container-${ct.container_no}`}
                title={ct.live ? "ETA fetched live from FindTeu" : "ETA from database (not tracked live yet)"}
                style={{ marginLeft: 12, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 8px", borderRadius: 999, border: "1px solid " + (ct.live ? "#0F766E" : "var(--jde-border)"), background: ct.live ? "#F0FDFA" : "#F8FAFC" }}>
                <span style={{ fontWeight: 700, color: "#0F172A", display: "inline-flex", alignItems: "center", gap: 4 }}><Package size={13} weight="bold" /> {ct.container_no}</span>
                {ct.eta_date && <span className="muted">ETA {fmtDate(ct.eta_date)}</span>}
                <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 0.3, padding: "1px 5px", borderRadius: 999, textTransform: "uppercase", background: ct.live ? "#0F766E" : "#E2E8F0", color: ct.live ? "white" : "#64748B" }}>{ct.live ? "Live" : "DB"}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* SUMMARY TILES */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
          <Tile label={isExp ? "Taxable (Export)" : "Taxable"} value={fmtCur(disp.taxable, disp.currency_code)} />
          <Tile label="GST" value={isExp ? "— (export)" : fmtCur(disp.gst, "INR")} color="#9333EA" />
          <Tile label="Charges" value={fmtCur(chargesInr, "INR")} />
          <Tile label={isExp ? "Export Invoice Total" : "G Total"} value={fmtCur(disp.gtotal, disp.currency_code)} color="#0F766E" />
        </div>

        <div className="jde-tab-strip">
          <button className={`jde-tab ${tab === "header" ? "active" : ""}`} onClick={() => setTab("header")} data-testid="tab-header">Header</button>
          <button className={`jde-tab ${tab === "dispatch" ? "active" : ""}`} onClick={() => setTab("dispatch")} data-testid="tab-dispatch">Dispatch / Transport</button>
          <button className={`jde-tab ${tab === "taxes" ? "active" : ""}`} onClick={() => setTab("taxes")} data-testid="tab-taxes">Tax & Charges</button>
          <button className={`jde-tab ${tab === "lines" ? "active" : ""}`} onClick={() => setTab("lines")} data-testid="tab-lines">Line Items ({inv.lines?.length || 0})</button>
        </div>

        <div className="jde-panel">
          {tab === "header" && (
            <div className="jde-panel-body">
              <table className="jde-detail-grid">
                <tbody>
                  <Row k="Sale No" v={inv.ms_sale_no} />
                  <Row k="Sale Noc" v={inv.ms_sale_noc} />
                  <Row k="Invoice No (Inv_no)" v={inv.ms_inv_no || "—"} />
                  <Row k="e-Way Bill No" v={inv.elec_ref_no || "—"} />
                  <Row k="Sale Date" v={fmtDate(inv.sale_date)} />
                  <Row k="Plant (CompCode)" v={inv.comp_code} />
                  <Row k="Sale Type" v={inv.sale_type} />
                  <Row k="Bill Type" v={inv.bill_type} />
                  <Row k="Customer" v={inv.customer_name} />
                  {tp && <Row k="Transfer Party" v={`${tp.tp_name || tp.tp_code} (${tp.tp_code})`} />}
                  <Row k="Party" v={inv.party || "—"} />
                  <Row k="Linked PO #" v={inv.ms_pono || "—"} />
                  <Row k="PO Date" v={fmtDate(inv.ms_podate)} />
                  <Row k="Currency" v={`${inv.currency || "INR"} · ExchRate ${inv.exch_rate || 1}`} />
                  <Row k="Created By" v={`${inv.create_by || "—"} on ${fmtDate(inv.create_time)}`} />
                  <Row k="Last sync from SAP" v={inv.ms_sync_at ? fmtDateTime(inv.ms_sync_at) : "—"} />
                </tbody>
              </table>
            </div>
          )}

          {tab === "dispatch" && (
            <div className="jde-panel-body">
              <table className="jde-detail-grid">
                <tbody>
                  <Row k="Transport Mode" v={d.trans_mode || "—"} />
                  <Row k="Transporter" v={d.trans || "—"} />
                  <Row k="Vehicle #" v={d.veh_no || "—"} />
                  <Row k="GR / LR #" v={d.gr_no || "—"} />
                  <Row k="GR Date" v={fmtDate(d.gr_date)} />
                  <Row k="Place of Supply" v={d.supply_place || "—"} />
                  <Row k="Supply Date" v={fmtDate(d.supply_dt)} />
                  <Row k="Supply Time" v={d.supply_time || "—"} />
                  <Row k="Ship To" v={d.ship_add || "—"} />
                  <Row k="Seal #" v={d.seal_no || "—"} />
                  <Row k="Weight Slip" v={d.weight_slip || "—"} />
                </tbody>
              </table>
            </div>
          )}

          {tab === "taxes" && (
            <div className="jde-panel-body">
              {isExp && (
                <div data-testid="disp-export-note" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "#F0FDFA", border: "1px solid #0F766E", fontSize: 12.5, color: "#0F766E" }}>
                  <strong>Export invoice</strong> — customer value is <strong>{fmtCur(disp.gtotal, disp.currency_code)}</strong> (Exch {inv.exch_rate || 1}). The GST / taxable figures below are the INR amounts on the Indian tax-document copy only.
                </div>
              )}
              <table className="jde-detail-grid">
                <tbody>
                  {isExp && <Row k="Export Invoice Value" v={<strong>{fmtCur(disp.gtotal, disp.currency_code)}</strong>} />}
                  <Row k={isExp ? "Total Taxable (INR)" : "Total Taxable"} v={fmtCur(t.ttaxable_amt, "INR")} />
                  <Row k="CGST" v={fmtCur(t.tcgst_amt, "INR")} />
                  <Row k="SGST" v={fmtCur(t.tsgst_amt, "INR")} />
                  <Row k="IGST" v={fmtCur(t.tigst_amt, "INR")} />
                  <Row k="GST Total" v={fmtCur(gst, "INR")} />
                  <Row k="Discount (TDis)" v={fmtCur(c.tdis_amt, "INR")} />
                  <Row k="Freight" v={fmtCur(c.freight_amt, "INR")} />
                  <Row k="Packing" v={fmtCur(c.packing_amt, "INR")} />
                  <Row k="Insurance" v={fmtCur(c.ins_amt, "INR")} />
                  <Row k="Other Charges" v={fmtCur(c.other_amt, "INR")} />
                  <Row k="TCS" v={`${c.tcs_per || 0}% = ${fmtCur(c.tcs_amt, "INR")}`} />
                  <Row k="Total" v={fmtCur(t.total, "INR")} />
                  <Row k={isExp ? "Grand Total (INR doc)" : "Grand Total"} v={<strong>{fmtCur(t.gtotal, "INR")}</strong>} />
                  <Row k="GST Payable" v={t.gst_payable || "—"} />
                  <Row k="Amount Received" v={fmtCur(inv.tot_rec, "INR")} />
                  <Row k="Paid Amount" v={fmtCur(inv.paid_amt, "INR")} />
                  <Row k="Due in (days)" v={`${inv.due_days || 0} · due ${fmtDate(inv.due_date)}`} />
                </tbody>
              </table>
            </div>
          )}

          {tab === "lines" && (
            <div className="jde-panel-body" style={{ padding: 0, overflowX: "auto" }}>
              <table className="jde-grid" style={{ minWidth: 1100 }} data-testid="dispatch-lines-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item Code</th>
                    <th>PO #</th>
                    <th>Description</th>
                    <th>HSN</th>
                    <th style={{ textAlign: "right" }}>Qty</th>
                    <th>Unit</th>
                    <th style={{ textAlign: "right" }}>Rate</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th style={{ textAlign: "right" }}>Discount</th>
                    <th style={{ textAlign: "right" }}>Taxable</th>
                    <th style={{ textAlign: "right" }}>CGST</th>
                    <th style={{ textAlign: "right" }}>SGST</th>
                    <th style={{ textAlign: "right" }}>IGST</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(inv.lines || []).map((ln, i) => (
                    <tr key={i} data-testid={`disp-line-${i}`}>
                      <td className="text-mono">{ln.sno || i + 1}</td>
                      <td className="text-mono">{ln.micode}<WmPart icode={ln.micode} /></td>
                      <td className="text-mono" data-testid={`dispatch-line-${i}-pono`}>{ln.pono || "—"}</td>
                      <td>{ln.name} {ln.part_inf ? <span className="muted">· {ln.part_inf}</span> : null}</td>
                      <td className="text-mono" style={{ fontSize: 11 }}>{ln.hsn_no || "—"}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{Number(ln.qty || 0).toFixed(2)}</td>
                      <td className="text-mono">{ln.unit || "—"}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{Number(ln.rate || 0).toFixed(2)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{Number(ln.amt || 0).toFixed(2)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{Number(ln.dis_amt || 0).toFixed(2)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{Number(ln.taxable_amt || 0).toFixed(2)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{ln.cgst_per || 0}% · {Number(ln.cgst_amt || 0).toFixed(2)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{ln.sgst_per || 0}% · {Number(ln.sgst_amt || 0).toFixed(2)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{ln.igst_per || 0}% · {Number(ln.igst_amt || 0).toFixed(2)}</td>
                      <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}>{Number(ln.tot_amt || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Tile({ label, value, color }) {
  return (
    <div style={{ background: "white", border: "1px solid var(--jde-border)", padding: "10px 12px", borderRadius: 4 }}>
      <div className="muted" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--jde-font-mono)", color: color || "#0F172A", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <tr>
      <td className="muted" style={{ width: 220, fontSize: 11, fontWeight: 600 }}>{k}</td>
      <td className="text-mono" style={{ fontSize: 12 }}>{v ?? "—"}</td>
    </tr>
  );
}
